import React from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useLocation } from "react-router-dom"

import { privateRoutes } from "router/routes"

import { useAuth, useAsideMenu, useTheme } from "hooks"
import FormElement from "components/Form/FormElement"
import { LanguageSelector } from "components/Elements/LanguageSelector"
import { MenuLinks } from "./components/MenuLinks"
import { 
    userPermissionList, 
    entitySpecificPermissionList, 
    operators, 
} from "utils/constants"
import Timer from "components/shared/Timer"
import { LegalDocuments } from ".."

import { 
    LogoutIcon,
    DiscussionIcon,
} from "components/Elements/Icons"

import "./AsideNav.scoped.scss"

const SUPPORT_TOKEN_DURATION = 60 * 60 * 1000 // in ms

export const AsideNav = React.memo(({ className }) => {
    const [operatorsUser, setOperatorsUser] = React.useState([])

    const { pathname } = useLocation()
    const { toggleAsideMenu } = useAsideMenu()
    const { user, entity, isConnectedFromSupport, logout, logoutAsSupport } = useAuth()
    const { currentTheme, toggleTheme } = useTheme()
    const { t } = useTranslation()

    const entityRole = entity?.entity_data?.entity_permission?.role
    const isMANAGEBIKES = entityRole === "MANAGEBIKES"
    const isMANAGEENTITIES = entityRole === "MANAGEENTITIES"

    // On pathname change, close the asideMenu
    React.useEffect(() => toggleAsideMenu(false), [pathname]) //eslint-disable-line

    // Filter allowed operators
    React.useEffect(() => {
        if (entity) {
            const allowedOperators = operators.filter(op => entity?.entity_data?.entity_permission?.operateurs.includes(op.id_api))
            setOperatorsUser(allowedOperators)
        }
    }, [entity])

    const handleOperatorChange = (event) => {
        const value = event.target.value
        toggleTheme(value)
    }

    return (
        <>
            {!currentTheme
                ?
                    <div className={"u-sticky-view"}>
                        <div className="c-main-content flex justify-center items-center">
                            <div className="c-spinner"></div>
                        </div>
                    </div>
                :
                    <aside className={"c-aside " + (className ? className : "")}>
                        <div className="p-5 h-20">
                            {isMANAGEBIKES && operatorsUser?.length > 1 &&
                                <FormElement
                                    value={{
                                        label: currentTheme?.name,
                                        value: currentTheme
                                    }}
                                    options={operatorsUser.map(operator => ({
                                        label: operator.name,
                                        value: operator
                                    }))}
                                    name="currentTheme"
                                    id="currentTheme"
                                    type="select"
                                    label={t("menu.operators")}
                                    className="flex items-center is-light"
                                    classNameLabel="flex-0 pr-5 text-white"
                                    selectClassname="flex-1 u-no-border"
                                    onChange={(e) => handleOperatorChange(e)}
                                />
                            }
                        </div>


                        <header className={"c-aside__header pb-4"}>
                            <NavLink to="/" className="u-block p-5">
                                {React.createElement(currentTheme?.logo, { className: "w-full text-white" })}
                            </NavLink>
                            <hr/>
                            <div className="text-xs px-2 my-5 text-center">
                                {isConnectedFromSupport && <p className="text-xs italic mb-2">{t("support.connected_as")}</p>}
                                {(user?.description?.prenom && user?.description?.nom)
                                    ? <div className="mb-2">{`${user?.description?.prenom} ${user?.description?.nom}`}</div>
                                    : <div className="mb-2">{`${user?.mail}`}</div>
                                }
                                <div>{t(`${(isMANAGEENTITIES ? entitySpecificPermissionList : userPermissionList).find(p => p.value === user?.permission)?.label}`)}</div>
                                {isConnectedFromSupport && (
                                    <div className="mt-2">
                                        <span className="italic mr-1">{t("support.remaining_time")}</span>
                                        <Timer
                                            className={"inline-block"}
                                            style={{ width: "40px" }}
                                            timeToWait={SUPPORT_TOKEN_DURATION}
                                            saveTimerInStorage={true}
                                        />
                                    </div>
                                )}
                            </div>
                        </header>

                        <MenuLinks />

                        <footer className="c-aside__footer pt-7">
                            <div className="c-aside__item px-5">
                                <LogoutIcon size={28} />
                                {!isConnectedFromSupport && (
                                    <button onClick={logout} className="p-5">
                                        {t("menu.logout")}
                                    </button>
                                )}
                                {isConnectedFromSupport && (
                                    <button onClick={logoutAsSupport} className="p-5">
                                        {t("support.logout_as")}
                                    </button>
                                )}
                            </div>

                            {currentTheme?.id !== "ocode" &&
                                <>
                                    <hr/>

                                    <div className={`c-aside__item px-5 uppercase ${pathname === privateRoutes.contact.path ? " is-active" : ""}`}>
                                        <DiscussionIcon size={35} />
                                        <NavLink to={privateRoutes.contact.path} className="p-5">{t("menu.contact_support")}</NavLink>
                                    </div>
                                    <LegalDocuments />
                                </>
                            }

                            <div className="flex justify-between px-5">
                                <LanguageSelector />

                                <div className="italic text-right m-2">
                                    Version: {process.env.REACT_APP_VERSION} - {process.env.REACT_APP_MODE === "development" && "TEST"}
                                </div>
                            </div>
                        </footer>

                    </aside>
            }
        </>
    )
})
